import { useEffect, useMemo, useState, type ReactNode } from 'react'

import { SignInAgainError } from '@/shared/api/api-error'
import { onUnauthorized } from '@/shared/api/unauthorized'
import { useRuntimeConfig } from '@/shared/config/runtime-config-context'

import { AuthStateProvider, type AuthState } from './auth-state'
import { isFreshSignInRequired, requireFreshSignIn } from './fresh-sign-in'
import { signOutForgettingFailure } from './kept-sign-in-failure'
import { isPublicPageShown } from './public-page'
import { redirectsAllowed, signOutRedirectAllowed } from './redirect-hold'
import { clearReturnPath, rememberReturnPath } from './return-path'
import {
  clearHopToken,
  DeviceClockError,
  displayNameOf,
  hopReturnedAt,
  isHopJustDone,
  isHopTokenChange,
  isHopTokenUsable,
  loadHopToken,
  readHopToken,
  renewalFor,
  reportDeviceClock,
  SignInRenewalRequiredError,
  signOutThroughSignInSite,
  startHop,
  type HopRenewal,
} from './sign-in-hop'
import { clearSignedInStorage } from './signed-in-storage'

// One renewal at a time, however many calls find the token expired together.
let renewing: Promise<void> | null = null

/**
 * Authentication on an address that signs in through the environment's sign-in site (SM-04): B2C's
 * access token a hop brought back stands in for MSAL's. Signing in, signing out and renewing an
 * expired token all go through the sign-in site; B2C's own session makes a renewal silent where it
 * can. A page with unsaved changes asks before a renewal leaves it, as it does for any move away.
 */
export function HopAuthProvider(props: { readonly children: ReactNode }) {
  const { apiBaseUrl, auth } = useRuntimeConfig()
  const { signInOrigin, redirectPath } = auth
  const [token, setToken] = useState(readHopToken)
  // Until the shared storage is read once the listeners are on, a sign-out in another tab since
  // bootstrap could be missed, so nothing is decided on the token yet.
  const [isReconciled, setReconciled] = useState(false)

  // Another tab signing out or in changes the shared token; a 401 may mean it already has.
  useEffect(() => {
    // From the shared storage, not this tab's copy: a sign-out in another tab may have been missed.
    // A load is answered with this tab's latest token, so a slow one never brings back an older one.
    const load = async () => {
      const loaded = await loadHopToken()
      if (loaded === null) {
        clearSignedInStorage()
      }
      // The same token kept as it was, so a 401 that changed nothing restarts nothing that depends on it.
      setToken((previous) =>
        previous !== null && loaded !== null && previous.accessToken === loaded.accessToken && previous.expiresOn === loaded.expiresOn
          ? previous
          : loaded,
      )
      setReconciled(true)
    }
    const reload = () => {
      void load()
    }
    const onStorage = (event: StorageEvent) => {
      if (isHopTokenChange(event)) {
        clearSignedInStorage()
        reload()
      }
    }
    window.addEventListener('storage', onStorage)
    const stopListening = onUnauthorized(reload)
    reload()
    return () => {
      window.removeEventListener('storage', onStorage)
      stopListening()
    }
  }, [])

  const state = useMemo<AuthState>(() => {
    // The API adds the site's own domain hint and policy.
    const hop = (loginHint: string, renewingFor: HopRenewal | null = null) =>
      startHop(apiBaseUrl, signInOrigin, { loginHint, isFresh: isFreshSignInRequired() }, renewingFor)

    return {
      mode: 'b2c',
      isResolving: !isReconciled,
      isAuthenticated: token !== null,
      user: token === null ? null : { displayName: displayNameOf(token.accessToken) },

      async signIn(email): Promise<void> {
        await redirectsAllowed()
        await hop(email.trim())
      },

      async signOut(): Promise<void> {
        clearReturnPath()
        clearSignedInStorage()
        await signOutForgettingFailure(redirectPath, async () => {
          // Before leaving, so a store another tab had under way is gone too.
          await clearHopToken()
          await signOutRedirectAllowed()
          signOutThroughSignInSite(signInOrigin)
        })
        // Signed out here too, should the person stay on a page with unsaved changes.
        setToken(null)
      },

      async forgetSignIn(): Promise<void> {
        clearReturnPath()
        clearSignedInStorage()
        requireFreshSignIn()
        const cleared = clearHopToken()
        setToken(null)
        await cleared
      },

      async getAccessToken(options): Promise<string | null> {
        const current = readHopToken()
        if (current === null) {
          return null
        }
        // A forced refresh, after a 401, renews even a token this clock still counts as good: the
        // device clock may be behind B2C's.
        if (isHopTokenUsable(current) && options?.isForcedRefresh !== true) {
          reportDeviceClock(true)
          return current.accessToken
        }
        // Just back from a hop: never round again. A token B2C gave with little time left is used
        // while it lasts. One that came back already past its end on this clock means the clock is
        // wrong, and the page says so; one that ran out since is renewed once the pause is over.
        if (isHopJustDone()) {
          if (current.expiresOn > Date.now()) {
            reportDeviceClock(true)
            return current.accessToken
          }
          if (current.expiresOn <= (hopReturnedAt() ?? 0)) {
            reportDeviceClock(false)
            throw new DeviceClockError()
          }
          throw new SignInRenewalRequiredError()
        }
        // A check the app makes on its own never takes the person off the page, nor goes without a token.
        if (options?.isSilent === true) {
          throw new SignInRenewalRequiredError()
        }

        // Before anything waits: only this person may come back, and nobody once any tab signs out.
        const renewal = renewalFor(current.accessToken)
        await redirectsAllowed()
        if (isPublicPageShown()) {
          if (options?.isWrite === true) {
            throw new SignInAgainError('The sign-in has expired, so the write was not sent.')
          }
          return null
        }

        // Renewed through the sign-in site, coming back to this page.
        const { pathname, search, hash } = window.location
        if (pathname !== redirectPath) {
          rememberReturnPath(`${pathname}${search}${hash}`)
        }
        renewing ??= hop('', renewal).finally(() => {
          renewing = null
        })
        await renewing
        // The page is leaving, or the person chose to stay on a page with unsaved changes: either
        // way this call is not sent without a token.
        throw new SignInRenewalRequiredError()
      },
    }
  }, [apiBaseUrl, signInOrigin, redirectPath, token, isReconciled])

  return <AuthStateProvider value={state}>{props.children}</AuthStateProvider>
}
